import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import Card from '../components/Card';
import { useViewExcelExport } from '../components/ExcelExportButton';
import { useRegisterPageExports } from '../exports/pageExports';
import { listAuditEvents, getAuditFilters } from '../api/audit';
import './AuditLogPage.css';

/**
 * Journal — qui a changé quoi, et quand.
 *
 * Écran de LECTURE, sans aucune action d'écriture : le serveur n'expose ni
 * modification ni suppression (un journal retouchable ne prouve rien). Réservé à
 * l'admin plateforme, la garde est côté serveur — le masquage du menu n'est
 * qu'un confort.
 *
 * Unité d'affichage = l'OPÉRATION, pas la ligne de base. Une requête qui change
 * trois champs d'un contrat se lit comme une seule ligne dépliable ; un import
 * Excel de 480 valeurs aussi. Sans ce repli, le journal serait illisible dès la
 * première semaine.
 */

const PAGE_SIZE = 100;

/* Horodatage : le serveur écrit de l'UTC sans suffixe de fuseau. Sans le « Z »,
   le navigateur lirait « 19:00 » comme une heure LOCALE et afficherait 19:00
   au lieu de 21:00 en été. */
function parseUtc(iso) {
  if (!iso) return null;
  return new Date(/[Z+]|-\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
}

const STAMP = new Intl.DateTimeFormat('de-CH', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
});

function Chevron() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export default function AuditLogPage({ addToast }) {
  const { t } = useTranslation();

  const [filters, setFilters] = useState({
    tenant_id: '', actor_email: '', module: '', action: '',
    table_name: '', date_from: '', date_to: '', search: '',
  });
  const [options, setOptions] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openOps, setOpenOps] = useState(() => new Set());
  /* Déplié de force le temps d'un export : le classeur se construit à partir du
     DOM RENDU, une ligne repliée n'y serait pas et le journal exporté mentirait
     par omission. */
  const [forceOpen, setForceOpen] = useState(false);
  const exportRef = useRef(null);

  /* ── Libellés ────────────────────────────────────────────────────────── */
  // Table et champ retombent sur leur nom technique quand aucun libellé n'est
  // traduit : on montre l'identifiant réel plutôt qu'un texte inventé.
  const tableLabel = useCallback(
    (name) => t(`audit:tables.${name}`, name), [t],
  );
  const fieldLabel = useCallback(
    (name) => (name ? t(`audit:fields.${name}`, name) : ''), [t],
  );

  const renderValue = useCallback((value, status) => {
    if (status === 'secret') return <em className="aud-note">{t('audit:values.secret')}</em>;
    if (status === 'document') return <em className="aud-note">{t('audit:values.document')}</em>;
    if (value === null || value === undefined || value === '') {
      return <span className="aud-empty">{t('audit:values.empty')}</span>;
    }
    if (value === 'true' || value === 'false') return t(`audit:values.${value}`);
    return value;
  }, [t]);

  /* ── Chargement ──────────────────────────────────────────────────────── */
  const load = useCallback(async (offset) => {
    setLoading(true);
    const res = await listAuditEvents({ ...filters, limit: PAGE_SIZE, offset });
    setLoading(false);
    if (!res.data) return;
    setTotal(res.data.total);
    setItems((prev) => (offset === 0 ? res.data.items : [...prev, ...res.data.items]));
  }, [filters]);

  useEffect(() => {
    // La recherche libre se tape lettre par lettre : on attend une pause avant
    // d'interroger le serveur. Les autres filtres partent tout de suite.
    const delay = filters.search ? 300 : 0;
    const id = setTimeout(() => { load(0); }, delay);
    return () => clearTimeout(id);
  }, [load, filters.search]);

  useEffect(() => {
    getAuditFilters().then((res) => { if (res.data) setOptions(res.data); });
  }, []);

  /* ── Regroupement par opération ──────────────────────────────────────── */
  const groups = useMemo(() => {
    const out = [];
    const index = new Map();
    items.forEach((ev) => {
      let group = index.get(ev.operation_id);
      if (!group) {
        group = { id: ev.operation_id, head: ev, events: [] };
        index.set(ev.operation_id, group);
        out.push(group);
      }
      group.events.push(ev);
    });
    return out;
  }, [items]);

  const toggle = (id) => setOpenOps((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /* ── Export ──────────────────────────────────────────────────────────── */
  const excel = useViewExcelExport({
    ref: exportRef,
    sheetName: t('audit:title'),
    fileBase: t('audit:title'),
    year: null,
    addToast,
  });
  const excelRun = useRef(null);
  excelRun.current = excel.run;
  const run = useCallback(async () => {
    setForceOpen(true);
    // Un rendu doit passer avant la lecture du DOM, sinon on exporte l'écran
    // encore replié.
    await new Promise((resolve) => setTimeout(resolve, 0));
    try { await excelRun.current(); } finally { setForceOpen(false); }
  }, []);
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run }], [run]);
  useRegisterPageExports(exportOptions);

  /* ── Rendu ───────────────────────────────────────────────────────────── */
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const anyFilter = Object.values(filters).some(Boolean);

  const select = (key, label, values, render) => (
    <label className="aud-filter">
      <span>{label}</span>
      <select value={filters[key]} onChange={set(key)}>
        <option value="">{t('audit:filters.all')}</option>
        {(values || []).map((v) => {
          const [value, text] = render(v);
          return <option key={value} value={value}>{text}</option>;
        })}
      </select>
    </label>
  );

  const tools = (
    <div className="aud-filters">
      {select('tenant_id', t('audit:filters.tenant'), options?.tenants,
        (v) => [v.id, v.name || v.id])}
      {select('actor_email', t('audit:filters.actor'), options?.actors, (v) => [v, v])}
      {select('module', t('audit:filters.module'), options?.modules,
        (v) => [v, t(`audit:modules.${v}`, v)])}
      {select('action', t('audit:filters.action'), options?.actions,
        (v) => [v, t(`audit:actions.${v}`, v)])}
      {select('table_name', t('audit:filters.table'), options?.tables,
        (v) => [v, tableLabel(v)])}
      <label className="aud-filter">
        <span>{t('audit:filters.from')}</span>
        <input type="date" value={filters.date_from} onChange={set('date_from')} />
      </label>
      <label className="aud-filter">
        <span>{t('audit:filters.to')}</span>
        <input type="date" value={filters.date_to} onChange={set('date_to')} />
      </label>
      <label className="aud-filter aud-filter--wide">
        <span>{t('audit:filters.search')}</span>
        <input type="search" value={filters.search} onChange={set('search')} />
      </label>
      {anyFilter && (
        <button type="button" className="aud-reset" onClick={() => setFilters({
          tenant_id: '', actor_email: '', module: '', action: '',
          table_name: '', date_from: '', date_to: '', search: '',
        })}>{t('audit:filters.reset')}</button>
      )}
    </div>
  );

  function summary(group) {
    const { head, events } = group;
    if (head.action === 'create') return t('audit:summary.created');
    if (head.action === 'delete') return t('audit:summary.deleted');
    // Restauration d'une sauvegarde : elle ne porte pas de champ, son ampleur
    // (le nombre de lignes rechargées) tient lieu de résumé.
    if (head.action === 'restore') {
      return t('audit:summary.restored', { count: Number(head.new_value) || 0 });
    }
    if (events.length === 1) {
      const ev = events[0];
      return (
        <span className="aud-inline">
          <span className="aud-field">{fieldLabel(ev.field_name)}</span>
          <span className="aud-before">{renderValue(ev.old_value, ev.value_status)}</span>
          <span className="aud-arrow" aria-hidden>→</span>
          <span className="aud-after">{renderValue(ev.new_value, ev.value_status)}</span>
        </span>
      );
    }
    return t('audit:summary.fields', { count: events.length });
  }

  function objectOf(group) {
    const labels = new Set(group.events.map((e) => e.record_label || e.record_id));
    const tables = new Set(group.events.map((e) => e.table_name));
    const table = tables.size === 1 ? tableLabel(group.head.table_name) : '';
    const label = labels.size === 1 ? [...labels][0] : `${labels.size}`;
    return { table, label: labels.size === 1 ? label : null, count: labels.size };
  }

  return (
    <PageShell
      timeless
      exportRef={exportRef}
      title={t('audit:title')}
      subtitle={t('audit:subtitle')}
      tools={tools}
      className="aud-page"
    >
      <Card flush>
        <table className="page-table aud-table">
          <thead>
            <tr>
              <th className="aud-col-when">{t('audit:columns.when')}</th>
              <th>{t('audit:columns.who')}</th>
              <th>{t('audit:columns.tenant')}</th>
              <th>{t('audit:columns.module')}</th>
              <th>{t('audit:columns.object')}</th>
              <th>{t('audit:columns.change')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const { head, events } = group;
              const open = forceOpen || openOps.has(group.id);
              const multiple = events.length > 1;
              const obj = objectOf(group);
              const stamp = parseUtc(head.created_at);
              return (
                <React.Fragment key={group.id}>
                  <tr
                    className={`aud-op${multiple ? ' lk-hi is-group' : ''}`}
                    onClick={multiple ? () => toggle(group.id) : undefined}
                  >
                    <td className="aud-col-when">
                      <span className="aud-gutter">
                        {multiple && (
                          <span className={`aud-chev${open ? ' is-open' : ''}`}>
                            <Chevron />
                          </span>
                        )}
                      </span>
                      {stamp ? STAMP.format(stamp) : ''}
                    </td>
                    <td>{head.actor_email || t('audit:actor.system')}</td>
                    <td>{head.tenant_name || ''}</td>
                    <td>{t(`audit:modules.${head.module}`, head.module)}</td>
                    <td>
                      <span className="aud-object">{obj.table}</span>
                      {obj.label && <span className="aud-record">{obj.label}</span>}
                    </td>
                    <td>{summary(group)}</td>
                  </tr>
                  {multiple && open && events.map((ev) => (
                    <tr key={ev.id} className="aud-detail">
                      <td className="aud-col-when" />
                      <td colSpan={2}>
                        <span className="aud-record">{ev.record_label || ev.record_id}</span>
                      </td>
                      <td>{fieldLabel(ev.field_name)}</td>
                      <td className="aud-before">{renderValue(ev.old_value, ev.value_status)}</td>
                      <td className="aud-after">{renderValue(ev.new_value, ev.value_status)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {!groups.length && !loading && (
              <tr><td colSpan={6} className="aud-blank">{t('audit:empty')}</td></tr>
            )}
            {loading && !groups.length && (
              <tr><td colSpan={6} className="aud-blank">{t('audit:loading')}</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <div className="aud-foot">
        <span className="aud-count">
          {t('audit:count.shown', { shown: items.length, total })}
        </span>
        {items.length < total && (
          <button type="button" className="aud-more" disabled={loading}
                  onClick={() => load(items.length)}>
            {loading ? t('audit:loading') : t('audit:count.more')}
          </button>
        )}
      </div>
    </PageShell>
  );
}
